import { tw } from 'utils/classnames'

export type TagSize = 'lg' | 'md' | 'sm' | 'xs'

export interface TagBaseProps extends React.ComponentProps<'span'> {
  size?: TagSize
}

const sizeClasses: Record<TagSize, string> = {
  lg: 'h-8 pr-[15px] pl-[15px] rounded-full studio-typography-12 [&:has(>svg,>img)]:pl-[11px]',
  md: 'h-6 pr-[13px] pl-[13px] rounded-full studio-typography-12 [&:has(>svg,>img)]:pl-[9px]',
  sm: 'py-px pr-[7px] pl-[7px] rounded-full studio-typography-12 [&:has(>svg,>img)]:pl-[5px]',
  xs: 'h-4 px-[5px] rounded studio-typography-10-capitals cap-trim',
}

/**
 * TagBase -- internal building block for TagLabel and TagStatus.
 *
 * Icons and images are passed as children alongside text. SVG and img
 * children are sized automatically via CSS child selectors (the same
 * approach Button uses). On xs size, svg/img children are hidden.
 *
 * Not exported from the package root; consumers should use TagLabel or TagStatus.
 *
 * @figma https://www.figma.com/design/wx3OX1hccLxomMLDn6QXDa/Studio-DS---Playground?node-id=2346-722
 */
export function TagBase({ size = 'lg', className, children, ...props }: TagBaseProps) {
  return (
    <span
      className={tw(
        'inline-flex shrink-0 items-center justify-center gap-1 ring-1 ring-inset whitespace-nowrap',
        sizeClasses[size],
        size === 'sm' ? '[&>svg]:size-3 [&>svg]:shrink-0' : '[&>svg]:size-4 [&>svg]:shrink-0',
        '[&>img]:size-6 [&>img]:shrink-0 [&>img]:-ml-[6px] [&>img]:rounded-full [&>img]:object-cover [&>img]:border [&>img]:border-theme_semantic_surface_border_color-subtle',
        size === 'xs' && '[&>svg]:hidden [&>img]:hidden',
        className,
      )}
      {...props}
    >
      {children}
    </span>
  )
}
